import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, Chip, TableBody, TableCell,
  TableHead, TableRow, TableSortLabel, Button, Alert, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  FormControlLabel, Switch, InputAdornment, Divider, TablePagination, Tooltip
} from '@mui/material';
import { AccountTree, Home, Add, Edit, Delete, Search, Clear, OpenInNew, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const CATEGORIES  = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];
const CATEGORY_LABEL = { Asset: 'Assets', Liability: 'Liabilities', Equity: 'Equity', Revenue: 'Income', Expense: 'Expenses' };
// Two top-level bands (BC "Income/Balance"). CATEGORIES order keeps each band contiguous.
const IB_OF    = { Asset: 'Balance Sheet', Liability: 'Balance Sheet', Equity: 'Balance Sheet', Revenue: 'Income', Expense: 'Income' };
const IB_LABEL = { 'Balance Sheet': 'BALANCE SHEET', Income: 'INCOME STATEMENT' };
const POSTING_TYPES = ['Posting', 'Heading', 'Begin-Total', 'End-Total', 'Total'];
const TYPE_COLOR = { Asset: 'primary', Liability: 'warning', Equity: 'secondary', Revenue: 'success', Expense: 'error' };

const emptyForm = {
  accountNo: '', accountName: '',incomeBalanceSheet:'', accountType: '', postingType: 'Posting',
  accountSubcategory: '', totaling: '', defaultDimensions: {}, isActive: true
};

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value ?? <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);

const GLAccountList = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts]   = useState([]);
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccountNo, setEditingAccountNo] = useState(null);
  const [form, setForm]           = useState(emptyForm);
  const [viewAccount, setViewAccount] = useState(null);
  const [deactivateDlg, setDeactivateDlg] = useState(null);
  const [search, setSearch]       = useState('');
  const [from, setFrom]           = useState('');
  const [to, setTo]               = useState('');
  const [page, setPage]           = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  // Column sort. null = default grouped Chart-of-Accounts view (Balance Sheet /
  // Income Statement → category → account no). Clicking a header switches to a
  // FLAT sorted list; clicking the active column cycles asc → desc → back to
  // grouped, so the structured outline is never lost.
  const [sortBy,  setSortBy]  = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [dimensions, setDimensions] = useState([]);   // active dimensions
  const [dimValues, setDimValues]   = useState({});   // { CODE: [values] }

  const { attemptClose, discardDialog } = useUnsavedGuard({
    open: dialogOpen, value: form, onClose: () => setDialogOpen(false),
  });

  // Load dimensions + their values (for the Default Dimensions editor)
  useEffect(() => {
    axios.get(`${API}/api/finance/dimensions`, { headers: hdrs() }).then(async r => {
      const ds = r.data || [];
      setDimensions(ds);
      const map = {};
      await Promise.all(ds.map(async d => {
        try { const v = await axios.get(`${API}/api/finance/dimension-values`, { headers: hdrs(), params: { dimension: d.code } }); map[d.code] = v.data || []; }
        catch { map[d.code] = []; }
      }));
      setDimValues(map);
    }).catch(() => {});
  }, []);

  const fetchAccounts = async () => {
    try {
      const params = {};
      if (from) params.from = from;
      if (to)   params.to   = to;
      const { data } = await axios.get(`${API}/api/finance/gl-accounts`, { headers: hdrs(), params });
      setAccounts(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load GL accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAccounts(); }, []); // eslint-disable-line
  const hasPeriod = !!(from || to);

  // Click a header: new column → asc; same column asc → desc; desc → back to grouped.
  const handleSort = (field) => {
    if (sortBy !== field)        { setSortBy(field); setSortDir('asc'); }
    else if (sortDir === 'asc')  { setSortDir('desc'); }
    else                         { setSortBy(null); setSortDir('asc'); }
  };

  // Search-filtered accounts (the date period, if any, is applied server-side).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return accounts.filter(a => !q ||
      String(a.accountNo).toLowerCase().includes(q) ||
      String(a.accountName).toLowerCase().includes(q));
  }, [accounts, search]);

  // Default view: grouped by band → category, account-number order within each.
  const grouped = useMemo(() => {
    const groups = CATEGORIES.map(cat => ({
      cat,
      ib: IB_OF[cat],
      rows: filtered
        .filter(a => a.accountType === cat)
        .sort((x, y) => String(x.accountNo).localeCompare(String(y.accountNo), undefined, { numeric: true })),
    })).filter(g => g.rows.length);
    // Flag the first category of each band so we can render a BALANCE SHEET / INCOME STATEMENT header.
    let lastIb = null;
    groups.forEach(g => { g.showBand = g.ib !== lastIb; lastIb = g.ib; });
    return groups;
  }, [filtered]);

  // Flatten grouped rows (preserving band + category order) so we can paginate,
  // then tag each visible row with whether it starts a new band/category — the
  // headers re-render at the top of each page slice.
  const flatRows = useMemo(() => {
    const out = [];
    grouped.forEach(g => g.rows.forEach(a => out.push({ ...a, _cat: g.cat, _ib: g.ib })));
    return out;
  }, [grouped]);

  const pageRows = useMemo(() => {
    const slice = flatRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    let prevCat = null, prevIb = null;
    return slice.map(r => {
      const showBand = r._ib !== prevIb;
      const showCat  = r._cat !== prevCat || showBand;
      prevIb = r._ib; prevCat = r._cat;
      return { ...r, showBand, showCat };
    });
  }, [flatRows, page, rowsPerPage]);

  // Flat sorted view — used only while a column sort is active. Ignores the
  // band/category outline and just orders every matching account by the column.
  const sortedRows = useMemo(() => {
    if (!sortBy) return [];
    const dir = sortDir === 'asc' ? 1 : -1;
    const val = (a) => {
      if (sortBy === 'balance')   return a.balance ?? 0;
      if (sortBy === 'netChange') return a.netChange ?? 0;
      return String(a[sortBy] ?? '').toLowerCase();
    };
    return [...filtered].sort((a, b) => {
      const x = val(a), y = val(b);
      if (typeof x === 'number') return (x - y) * dir;
      return x.localeCompare(y, undefined, { numeric: true }) * dir;
    });
  }, [filtered, sortBy, sortDir]);

  const usingSort  = !!sortBy;
  const totalCount = usingSort ? sortedRows.length : flatRows.length;
  const sortedPage = useMemo(
    () => (usingSort ? sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : []),
    [usingSort, sortedRows, page, rowsPerPage]
  );

  useEffect(() => { setPage(0); }, [search, sortBy, sortDir]);

  const isStructure = (a) => (a.postingType || 'Posting') !== 'Posting';

  const handleCreate = () => { setEditingAccountNo(null); setForm(emptyForm); setDialogOpen(true); };
  const handleEdit = (a) => {
    setEditingAccountNo(a.accountNo);
    setForm({
      accountNo: a.accountNo, accountName: a.accountName, accountType: a.accountType,
      postingType: a.postingType || 'Posting', accountSubcategory: a.accountSubcategory || '',
      totaling: a.totaling || '', defaultDimensions: a.defaultDimensions || {}, isActive: a.isActive ?? true,
    });
    setDialogOpen(true);
  };

  const confirmAction = async () => {
    try {
      await axios.delete(`${API}/api/finance/gl-accounts/${deactivateDlg}`, { headers: hdrs() });
      setDeactivateDlg(null);
      fetchAccounts();
    } catch (err) {
      setDeactivateDlg(null);
      setError(err.response?.data?.error || 'Failed to deactivate account');
    }
  };

  const handleSave = async () => {
    if (!editingAccountNo && !form.accountNo.trim()) return setError('Account No is required');
    if (!form.accountName.trim() || !form.accountType) return setError('Account Name and Account Category are required');
    const payload = {
      accountName: form.accountName, accountType: form.accountType, postingType: form.postingType,
      accountSubcategory: form.accountSubcategory, totaling: form.totaling,
      defaultDimensions: form.defaultDimensions || {},
    };
    try {
      if (editingAccountNo) {
        await axios.put(`${API}/api/finance/gl-accounts/${editingAccountNo}`, { ...payload, isActive: form.isActive }, { headers: hdrs() });
      } else {
        await axios.post(`${API}/api/finance/gl-accounts`, { accountNo: form.accountNo, ...payload }, { headers: hdrs() });
      }
      setDialogOpen(false);
      setError('');
      fetchAccounts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save GL account');
    }
  };

  // One account row — shared by the grouped and the flat-sorted views.
  const renderAccountRow = (a) => {
    const struct = isStructure(a);
    return (
      <TableRow hover key={a.accountNo}
        sx={{ cursor: 'pointer', bgcolor: struct ? 'grey.50' : undefined }}
        onClick={() => setViewAccount(a)}>
        <TableCell sx={{ pl: 1 + (a.indentation || 0) * 2.5, fontWeight: struct ? 700 : 400 }}>
          {a.accountNo}
        </TableCell>
        <TableCell sx={{ fontWeight: struct ? 700 : 400 }}>{a.accountName}</TableCell>
        <TableCell>
          <Chip label={a.postingType || 'Posting'} size="small"
            variant={struct ? 'filled' : 'outlined'}
            color={struct ? 'default' : (TYPE_COLOR[a.accountType] || 'default')} />
        </TableCell>
        <TableCell><Typography variant="caption" color="text.secondary">{a.accountSubcategory || '—'}</Typography></TableCell>
        {hasPeriod && (
          <TableCell align="right" sx={{ fontWeight: struct ? 700 : 400 }}>
            {fmt(a.netChange ?? 0)}
          </TableCell>
        )}
        <TableCell align="right" onClick={e => e.stopPropagation()}>
          {struct ? (
            <Typography variant="body2" fontWeight={800}>{fmt(a.balance ?? 0)}</Typography>
          ) : (
            <Chip
              label={(a.balance ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              color={(a.balance ?? 0) >= 0 ? 'success' : 'error'}
              size="small" variant="outlined"
              onClick={() => navigate(`/gl-accounts/${a.accountNo}`)}
              sx={{ cursor: 'pointer', fontWeight: 600 }} />
          )}
        </TableCell>
        <TableCell onClick={e => e.stopPropagation()}>
          <IconButton size="small" onClick={() => handleEdit(a)} title="Edit"><Edit fontSize="small" /></IconButton>
          <IconButton size="small" color="warning" title="Deactivate"
            onClick={() => setDeactivateDlg(a.accountNo)}
            disabled={!struct && !!a.balance}><Delete fontSize="small" /></IconButton>
          {!struct && (
            <IconButton size="small" title="Entries"
              onClick={() => navigate(`/gl-accounts/${a.accountNo}`)}><OpenInNew fontSize="small" /></IconButton>
          )}
        </TableCell>
      </TableRow>
    );
  };

  // Sortable column header cell.
  const SortHead = ({ field, label, align }) => (
    <TableCell align={align} sortDirection={sortBy === field ? sortDir : false}>
      <TableSortLabel
        active={sortBy === field}
        direction={sortBy === field ? sortDir : 'asc'}
        onClick={() => handleSort(field)}>
        <strong>{label}</strong>
      </TableSortLabel>
    </TableCell>
  );

  const isTotalType = ['Total', 'End-Total', 'Begin-Total'].includes(form.postingType);

  if (loading) return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading chart of accounts...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1150, mx: 'auto', mt: 4, mb: 6 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <AccountTree sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" color="primary">Chart of Accounts</Typography>
            </Box>
            <Box>
              <IconButton onClick={() => navigate('/dashboard')} title="Dashboard"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, mr: 1 }}>
                <Home />
              </IconButton>
              <IconButton onClick={handleCreate} title="New GL Account"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Add />
              </IconButton>
            </Box>
          </Box>

          {/* Search + period (Net Change) */}
          <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
            <TextField size="small" placeholder="Search by No. or Name…" value={search}
              onChange={e => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}><Clear fontSize="small" /></IconButton>
                  </InputAdornment>
                )
              }} />
            <TextField size="small" type="date" label="Net Change from" value={from}
              onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" label="to" value={to}
              onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Button size="small" variant="outlined" onClick={fetchAccounts}>Apply</Button>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {totalCount === 0 ? (
            <Typography color="text.secondary">
              {accounts.length === 0 ? 'No GL accounts found.' : 'No accounts match your search.'}
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortHead field="accountNo"   label="No" />
                    <SortHead field="accountName" label="Name" />
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Subcategory</strong></TableCell>
                    {hasPeriod && <SortHead field="netChange" label="Net Change" align="right" />}
                    <SortHead field="balance" label={`Balance (${sym()})`} align="right" />
                    <TableCell>
                      <strong>Actions</strong>
                      {usingSort && (
                        <Tooltip title="Back to grouped Chart-of-Accounts view">
                          <Button size="small" onClick={() => { setSortBy(null); setSortDir('asc'); }}
                            sx={{ ml: 1, minWidth: 0, textTransform: 'none', fontSize: 11 }}>
                            Grouped
                          </Button>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usingSort
                    ? sortedPage.map(a => renderAccountRow(a))
                    : pageRows.map(a => (
                        <React.Fragment key={a.accountNo}>
                          {a.showBand && (
                            <TableRow sx={{ bgcolor: 'primary.main' }}>
                              <TableCell colSpan={hasPeriod ? 7 : 6}>
                                <Typography variant="subtitle2" fontWeight={900}
                                  sx={{ color: 'common.white', letterSpacing: 1 }}>
                                  {IB_LABEL[a._ib]}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                          {a.showCat && (
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell colSpan={hasPeriod ? 7 : 6}>
                                <Typography variant="subtitle2" fontWeight={800} color="primary">
                                  {CATEGORY_LABEL[a._cat]}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                          {renderAccountRow(a)}
                        </React.Fragment>
                      ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[25, 50, 100, 200]}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* View card */}
      <Dialog open={Boolean(viewAccount)} onClose={() => setViewAccount(null)} maxWidth="xs" fullWidth>
        {viewAccount && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <AccountTree color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{viewAccount.accountName}</Typography>
                  <Typography variant="caption" color="text.secondary">{viewAccount.accountNo}</Typography>
                </Box>
                <Box ml="auto">
                  <Chip label={viewAccount.postingType || 'Posting'} size="small" variant="outlined"
                    color={TYPE_COLOR[viewAccount.accountType] || 'default'} />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle2" color="primary" gutterBottom>Account Details</Typography>
              <InfoRow label="Account No"       value={viewAccount.accountNo} />
              <InfoRow label="Account Name"     value={viewAccount.accountName} />
              <InfoRow label="Account Category" value={CATEGORY_LABEL[viewAccount.accountType] || viewAccount.accountType} />
              <InfoRow label="Account Type"     value={viewAccount.postingType || 'Posting'} />
              <InfoRow label="Subcategory"      value={viewAccount.accountSubcategory} />
              {viewAccount.totaling && <InfoRow label="Totaling" value={viewAccount.totaling} />}
              {viewAccount.defaultDimensions && Object.keys(viewAccount.defaultDimensions).length > 0 && (
                <Box display="flex" gap={1} mb={0.5} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 500 }}>Default Dimensions</Typography>
                  {Object.entries(viewAccount.defaultDimensions).filter(([, v]) => v).map(([k, v]) => (
                    <Chip key={k} size="small" variant="outlined" color="primary" label={`${k}: ${v}`} />
                  ))}
                </Box>
              )}
              <InfoRow label="Status"           value={viewAccount.isActive === false ? 'Inactive' : 'Active'} />

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" color="primary" gutterBottom>Balance</Typography>
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 500 }}>Current Balance</Typography>
                <Chip label={`${sym()} ${fmt(viewAccount.balance ?? 0)}`}
                  color={(viewAccount.balance ?? 0) >= 0 ? 'success' : 'error'}
                  size="small" variant="outlined" />
              </Box>
            </DialogContent>
            <DialogActions>
              {(viewAccount.postingType || 'Posting') === 'Posting' && (
                <Button startIcon={<OpenInNew />}
                  onClick={() => { setViewAccount(null); navigate(`/gl-accounts/${viewAccount.accountNo}`); }}>
                  View Entries
                </Button>
              )}
              <Button onClick={() => { setViewAccount(null); handleEdit(viewAccount); }}>Edit</Button>
              <Button variant="outlined" onClick={() => setViewAccount(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Deactivate confirm */}
      <Dialog open={Boolean(deactivateDlg)} onClose={() => setDeactivateDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
          <WarningAmber color="warning" /> Deactivate Account
        </DialogTitle>
        <DialogContent>
          <Typography>Deactivate GL Account <strong>{deactivateDlg}</strong>? It will be hidden from new transactions.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDlg(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={confirmAction}>Deactivate</Button>
        </DialogActions>
      </Dialog>

      {/* Create / Edit */}
      <Dialog open={dialogOpen} onClose={attemptClose} fullWidth maxWidth="sm">
        <DialogTitle>{editingAccountNo ? `Edit — ${editingAccountNo}` : 'New GL Account'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Account No" value={form.accountNo}
              onChange={e => setForm({ ...form, accountNo: e.target.value })}
              disabled={!!editingAccountNo} fullWidth size="small" />
            <TextField label="Account Name" value={form.accountName}
              onChange={e => setForm({ ...form, accountName: e.target.value })} fullWidth size="small" />
            <Box display="flex" gap={2}>
              <TextField select label="Account Category" value={form.accountType}
                onChange={e => setForm({ ...form, accountType: e.target.value })} fullWidth size="small">
                <MenuItem value=""><em>Select category</em></MenuItem>
                {CATEGORIES.map(t => <MenuItem key={t} value={t}>{CATEGORY_LABEL[t]}</MenuItem>)}
              </TextField>
              <TextField select label="Account Type" value={form.postingType}
                onChange={e => setForm({ ...form, postingType: e.target.value })} fullWidth size="small"
                helperText="Only 'Posting' accounts are postable">
                {POSTING_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Box>
            <TextField label="Subcategory (optional)" value={form.accountSubcategory}
              onChange={e => setForm({ ...form, accountSubcategory: e.target.value })} fullWidth size="small"
              placeholder="e.g. Current Assets, Income · Product Sales" />
            {isTotalType && (
              <TextField label="Totaling" value={form.totaling}
                onChange={e => setForm({ ...form, totaling: e.target.value })} fullWidth size="small"
                placeholder="e.g. 1000..1998 or 4000..4998|6000..6998"
                helperText="Account-number range(s) this total rolls up" />
            )}
            {!isTotalType && dimensions.length > 0 && (
              <>
                <Divider textAlign="left" sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">Default Dimensions (auto-stamped on postings)</Typography>
                </Divider>
                {dimensions.map(d => (
                  <TextField key={d.code} select size="small" fullWidth
                    label={d.name}
                    value={(form.defaultDimensions && form.defaultDimensions[d.code]) || ''}
                    onChange={e => setForm(f => ({ ...f, defaultDimensions: { ...(f.defaultDimensions || {}), [d.code]: e.target.value } }))}>
                    <MenuItem value=""><em>— None —</em></MenuItem>
                    {(dimValues[d.code] || []).map(v => (
                      <MenuItem key={v.value} value={v.value}>{v.value} — {v.name}</MenuItem>
                    ))}
                  </TextField>
                ))}
              </>
            )}
            {editingAccountNo && (
              <FormControlLabel
                control={<Switch checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />}
                label="Active" />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
      {discardDialog}
    </Box>
  );
};

export default GLAccountList;
